import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, BadgeCheck, BriefcaseBusiness, Building2, MapPin, ShieldCheck, Star, TrendingUp } from "lucide-react";
import { getPartnerProfile } from "@/lib/services.functions";
import { PublicNav } from "@/components/site/PublicNav";
import { Footer } from "@/components/site/Footer";
import { gbp, ukDate } from "@/lib/format";
import { PublicBadgeGrid } from "@/components/achievements/PublicBadgeGrid";


export const Route = createFileRoute("/partners/$partnerId")({
  head: () => ({
    meta: [
      { title: "Verified partner — Shields" },
      {
        name: "description",
        content:
          "See this verified Shields partner's services, work portfolio and verified customer reviews.",
      },
      { property: "og:title", content: "Verified partner — Shields" },
      {
        property: "og:description",
        content: "Work photos, services and verified reviews for this Shields partner.",
      },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PartnerProfilePage,
});

function Stars({ rating, className }: { rating: number; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-0.5 ${className ?? ""}`} aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-4 w-4 ${n <= rating ? "fill-warning text-warning" : "text-muted-foreground/30"}`}
          aria-hidden
        />
      ))}
    </span>
  );
}

function PartnerProfilePage() {
  const { partnerId } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["partner-profile", partnerId],
    queryFn: () => getPartnerProfile({ data: { partnerId } }),
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-muted-foreground">This partner profile isn't available.</p>
        <Link to="/partners" className="font-semibold text-primary underline">
          Browse verified partners
        </Link>
      </div>
    );
  }

  const {
    profile,
    workPhotos,
    reviews,
    avatarUrl,
    username,
    priceStats,
    overallAvgPence,
    completedJobs,
    services,
    businessDetails,
  } = data;

  return (
    <div className="min-h-screen">
      <PublicNav />
      <main className="section-pad mx-auto max-w-4xl py-10">
        <Link
          to="/partners"
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> All partners
        </Link>

        <section className="card-surface mt-6 p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full border-2 border-primary/30 bg-[image:var(--gradient-premium)] shadow-[var(--shadow-glow)]">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={`${profile.trading_name} profile photo`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="grid h-full w-full place-items-center font-display text-2xl font-extrabold text-primary-foreground">
                    {profile.trading_name.slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <h1 className="font-display text-2xl font-extrabold sm:text-3xl">{profile.trading_name}</h1>
                {username && (
                  <Link to="/u/$username" params={{ username }} className="text-sm font-semibold text-primary hover:underline">
                    @{username}
                  </Link>
                )}
                {profile.headline && <p className="mt-1 font-semibold">{profile.headline}</p>}
              </div>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-3 py-1 text-xs font-bold text-success">
              <BadgeCheck className="h-3.5 w-3.5" aria-hidden /> Verified by Shields
            </span>
          </div>
          {profile.rating_count > 0 && (
            <p className="mt-3 flex items-center gap-2 text-sm font-semibold">
              <Stars rating={Math.round(Number(profile.rating_avg))} />
              {Number(profile.rating_avg).toFixed(1)} · {profile.rating_count} verified{" "}
              {profile.rating_count === 1 ? "review" : "reviews"}
            </p>
          )}
          {profile.intro && (
            <p className="mt-4 whitespace-pre-wrap text-sm text-muted-foreground">{profile.intro}</p>
          )}
          {profile.service_areas && (
            <p className="mt-4 flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" aria-hidden /> {profile.service_areas}
            </p>
          )}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <ProfileFact
              icon={BriefcaseBusiness}
              label="Completed jobs"
              value={completedJobs.toLocaleString("en-GB")}
            />
            <ProfileFact
              icon={Star}
              label="Customer rating"
              value={profile.rating_count > 0 ? `${Number(profile.rating_avg).toFixed(1)} / 5` : "New"}
            />
            <ProfileFact
              icon={TrendingUp}
              label="Experience"
              value={businessDetails?.yearsExperience ? `${businessDetails.yearsExperience}+ years` : "Ask partner"}
            />
            <ProfileFact
              icon={ShieldCheck}
              label="Shields status"
              value="Verified"
            />
          </div>

          {services.length > 0 && (
            <section className="mt-7 border-t border-border pt-6" aria-labelledby="partner-services">
              <h2 id="partner-services" className="font-display text-lg font-bold">Services offered</h2>
              <ul className="mt-3 grid gap-3 sm:grid-cols-2">
                {services.map((service) => (
                  <li key={service.slug} className="rounded-2xl border border-border bg-muted/30 p-4">
                    <p className="font-bold text-foreground">{service.name}</p>
                    {service.description && (
                      <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                        {service.description}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="mt-6 grid gap-3 border-t border-border pt-5 text-sm text-muted-foreground sm:grid-cols-2">
            <p className="flex items-center gap-2">
              <BadgeCheck className="h-4 w-4 text-success" aria-hidden />
              Identity, insurance and competence checked
            </p>
            {businessDetails?.companyNumber && (
              <p className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" aria-hidden />
                Company no. {businessDetails.companyNumber}
              </p>
            )}
            {businessDetails?.insuranceExpires && (
              <p className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" aria-hidden />
                Insurance checked through {ukDate(businessDetails.insuranceExpires)}
              </p>
            )}
            <p>Shields member since {ukDate(profile.created_at)}</p>
          </div>
          <Link to="/book" className="btn-hero mt-6 w-full sm:w-auto">
            Request a quote
          </Link>
        </section>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold">
            <TrendingUp className="h-5 w-5 text-primary" aria-hidden /> Typical prices
          </h2>
          {priceStats.length === 0 ? (
            <p className="card-surface mt-4 p-6 text-sm text-muted-foreground">
              No completed jobs with an agreed price yet, so there's no price history to show.
            </p>
          ) : (
            <>
              <div className="card-surface mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-2 p-6">
                <span className="font-display text-3xl font-extrabold text-primary">
                  {gbp(overallAvgPence ?? 0)}
                </span>
                <span className="text-sm text-muted-foreground">
                  average agreed price across {completedJobs} completed{" "}
                  {completedJobs === 1 ? "job" : "jobs"}
                </span>
              </div>
              <ul className="mt-3 grid gap-3 sm:grid-cols-2">
                {priceStats.map((p) => (
                  <li key={p.serviceId} className="card-surface p-5">
                    <p className="font-semibold">{p.serviceName}</p>
                    <p className="mt-1 font-display text-xl font-extrabold">{gbp(p.avgPricePence)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {p.jobs} {p.jobs === 1 ? "job" : "jobs"} · usually {gbp(p.minPricePence)}–
                      {gbp(p.maxPricePence)}
                    </p>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-muted-foreground">
                Prices are what customers actually agreed with this partner. Your own quote depends on
                size, access and condition.
              </p>
            </>
          )}
        </section>

        <div className="mt-10">
          <PublicBadgeGrid userId={partnerId} audience="partner" heading="Achievements earned" />
        </div>

        <section className="mt-10">

          <h2 className="font-display text-xl font-bold">Work portfolio</h2>
          {workPhotos.length === 0 ? (
            <p className="card-surface mt-4 p-6 text-sm text-muted-foreground">
              This partner hasn't added photos of their work yet.
            </p>
          ) : (
            <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {workPhotos.map((p) => (
                <li key={p.id} className="card-surface overflow-hidden">
                  {p.url ? (
                    <img
                      src={p.url}
                      alt={p.caption ?? `Work by ${profile.trading_name}`}
                      className="aspect-square w-full object-cover"
                      loading="lazy"
                    />
                  ) : null}
                  {p.caption && <p className="p-3 text-xs text-muted-foreground">{p.caption}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-10">
          <h2 className="font-display text-xl font-bold">Verified reviews</h2>
          {reviews.length === 0 ? (
            <p className="card-surface mt-4 p-6 text-sm text-muted-foreground">
              No reviews yet — reviews can only be left by customers after a completed booking.
            </p>
          ) : (
            <ul className="mt-4 space-y-4">
              {reviews.map((r) => (
                <li key={r.id} className="card-surface p-6">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Stars rating={r.rating} />
                    <span className="text-xs text-muted-foreground">
                      {r.reviewer_name} · {ukDate(r.created_at)}
                    </span>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm">{r.body}</p>
                  {r.photos.length > 0 && (
                    <ul className="mt-3 flex flex-wrap gap-2">
                      {r.photos.map((url, i) =>
                        url ? (
                          <li key={i}>
                            <img
                              src={url}
                              alt={`Review photo ${i + 1} by ${r.reviewer_name}`}
                              className="h-24 w-24 rounded-lg object-cover"
                              loading="lazy"
                            />
                          </li>
                        ) : null,
                      )}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}


function ProfileFact({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Star;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-background p-4">
      <Icon className="h-5 w-5 text-primary" aria-hidden />
      <p className="mt-3 text-lg font-extrabold text-foreground">{value}</p>
      <p className="mt-1 text-xs font-semibold text-muted-foreground">{label}</p>
    </div>
  );
}
